import React, { useState } from 'react';
import {
    Table, Button, Space, Typography, Tag, message, Modal, Form,
    Input, InputNumber, Select, Drawer, Alert, Tree, Divider, Switch, Card, Breadcrumb, Tooltip
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';

const { Title, Text } = Typography;

// 广告类型枚举（用于同一分群内广告位类型去重）
const AD_TYPE_MAP = {
    splash: { label: '开屏', color: 'blue' },
    interstitial: { label: '插屏', color: 'purple' },
    banner: { label: 'Banner', color: 'green' },
    native: { label: '原生', color: 'orange' },
    rewarded: { label: '激励视频', color: 'red' },
};

// ──────────────────────────────────────────────
//  Mock 数据
// ──────────────────────────────────────────────

// TODO: 联调时替换为接口数据 — 平台代运营 App（运营方式=平台代运营），两级：App → 系统版本-区域
const MOCK_APP_TREE = [
    {
        key: 'app_test_001', title: 'test-牵心Pro',
        children: [
            { key: 'app_test_001__android_mainland', title: 'Android-中国大陆' },
            { key: 'app_test_001__ios_overseas', title: 'iOS-海外' },
        ],
    },
    {
        key: 'app_test_002', title: 'test-鹤梦之家',
        children: [
            { key: 'app_test_002__android_global', title: 'Android-全球' },
            { key: 'app_test_002__ios_mainland', title: 'iOS-中国大陆' },
        ],
    },
    {
        key: 'app_test_003', title: 'test-HomeStar',
        children: [
            { key: 'app_test_003__android_mainland', title: 'Android-中国大陆' },
        ],
    },
    {
        key: 'app_test_004', title: 'test-云视通',
        children: [
            { key: 'app_test_004__ios_overseas', title: 'iOS-海外' },
            { key: 'app_test_004__android_overseas', title: 'Android-海外' },
        ],
    },
];

// 全部树节点 key（全选用）
const ALL_APP_KEYS = [];
const collectKeys = (nodes) => {
    nodes.forEach((n) => {
        ALL_APP_KEYS.push(n.key);
        if (n.children) collectKeys(n.children);
    });
};
collectKeys(MOCK_APP_TREE);

// 新建策略默认勾选第一个 App 及其版本-区域，减少演示配置负担
const DEFAULT_CHECKED_KEYS = ['app_test_001', 'app_test_001__android_mainland', 'app_test_001__ios_overseas'];

// TODO: 联调时替换为接口数据 — 用户分群来源于数仓，只读；选择分群时按需查询过滤
const MOCK_USER_SEGMENTS = [
    { value: 'user_group_high_value', label: '高价值用户', count: 12480 },
    { value: 'user_group_active', label: '活跃用户', count: 38920 },
    { value: 'user_group_silent', label: '沉默用户', count: 5210 },
    { value: 'user_group_new_reg', label: '新注册用户', count: 15640 },
    { value: 'user_group_paid', label: '付费用户', count: 4250 },
];

// TODO: 联调时替换为接口数据 — 全局广告位库（来源：运营管理 → 广告配置管理）
const MOCK_GLOBAL_SLOTS = [
    {
        slotId: 'test-gslot-001', name: 'test-开屏广告位', type: 'splash',
        sources: [
            { name: 'test-开屏广告-主源', adId: 'S47382' },
            { name: 'test-开屏广告-备源', adId: 'S52891' },
        ],
    },
    {
        slotId: 'test-gslot-002', name: 'test-插屏广告位', type: 'interstitial',
        sources: [
            { name: 'test-插屏广告-主源', adId: 'S61234' },
        ],
    },
    {
        slotId: 'test-gslot-003', name: 'test-Banner广告位', type: 'banner',
        sources: [
            { name: 'test-Banner广告-主源', adId: 'S32891' },
            { name: 'test-Banner广告-备源', adId: 'S33892' },
        ],
    },
    {
        slotId: 'test-gslot-004', name: 'test-激励视频广告位', type: 'rewarded',
        sources: [
            { name: 'test-激励视频-主源', adId: 'S89012' },
        ],
    },
    {
        slotId: 'test-gslot-005', name: 'test-原生广告位', type: 'native',
        sources: [
            { name: 'test-原生广告-主源', adId: 'S34567' },
        ],
    },
];

// TODO: 联调时替换为接口数据 — 广告投放策略
// 结构：appConfigs = { [分群编码]: { cooldown: 秒, slots: [广告位配置] } }
// 广告位与频控配置为全局一份，所有已选 App 统一生效；广告源按「App-系统-区域」组合分别配置（非必填）；「分群 + App-系统-区域」组合在策略表中唯一
const MOCK_STRATEGIES = [
    {
        key: '1', name: 'test-牵心Pro活跃用户策略', strategyId: 'test-gstr-001',
        appKeys: ['app_test_001', 'app_test_001__android_mainland', 'app_test_001__ios_overseas'],
        appConfigs: {
            user_group_active: {
                cooldown: 30,
                slots: [
                    { slotId: 'test-gslot-001', slotName: 'test-开屏广告位', appSources: { app_test_001__android_mainland: 'test-开屏广告-主源', app_test_001__ios_overseas: 'test-开屏广告-备源' }, enabled: true, freq: 10, interval: 15, refresh: 30 },
                    { slotId: 'test-gslot-002', slotName: 'test-插屏广告位', appSources: { app_test_001__android_mainland: 'test-插屏广告-主源', app_test_001__ios_overseas: 'test-插屏广告-主源' }, enabled: true, freq: 8, interval: 20, refresh: 45 },
                ],
            },
            user_group_new_reg: {
                cooldown: 60,
                slots: [
                    { slotId: 'test-gslot-001', slotName: 'test-开屏广告位', appSources: { app_test_001__android_mainland: 'test-开屏广告-主源', app_test_001__ios_overseas: 'test-开屏广告-主源' }, enabled: true, freq: 5, interval: 30, refresh: 60 },
                ],
            },
        },
    },
    {
        key: '2', name: 'test-鹤梦之家高价值策略', strategyId: 'test-gstr-002',
        appKeys: ['app_test_002', 'app_test_002__android_global', 'app_test_002__ios_mainland'],
        appConfigs: {
            user_group_high_value: {
                cooldown: 120,
                slots: [
                    { slotId: 'test-gslot-004', slotName: 'test-激励视频广告位', appSources: { app_test_002__android_global: 'test-激励视频-主源', app_test_002__ios_mainland: 'test-激励视频-主源' }, enabled: true, freq: 3, interval: 120, refresh: 180 },
                    { slotId: 'test-gslot-005', slotName: 'test-原生广告位', appSources: { app_test_002__android_global: 'test-原生广告-主源', app_test_002__ios_mainland: 'test-原生广告-主源' }, enabled: true, freq: 6, interval: 30, refresh: 60 },
                ],
            },
        },
    },
    {
        key: '3', name: 'test-HomeStar沉默用户策略', strategyId: 'test-gstr-003',
        appKeys: ['app_test_003', 'app_test_003__android_mainland'],
        appConfigs: {
            user_group_silent: {
                cooldown: 300,
                slots: [
                    { slotId: 'test-gslot-001', slotName: 'test-开屏广告位', appSources: { app_test_003__android_mainland: 'test-开屏广告-主源' }, enabled: true, freq: 2, interval: 180, refresh: 360 },
                ],
            },
        },
    },
    {
        key: '4', name: 'test-云视通付费用户策略', strategyId: 'test-gstr-004',
        appKeys: ['app_test_004', 'app_test_004__ios_overseas', 'app_test_004__android_overseas'],
        appConfigs: {
            user_group_paid: {
                cooldown: 60,
                slots: [
                    { slotId: 'test-gslot-004', slotName: 'test-激励视频广告位', appSources: { app_test_004__ios_overseas: 'test-激励视频-主源', app_test_004__android_overseas: 'test-激励视频-主源' }, enabled: true, freq: 3, interval: 120, refresh: 180 },
                ],
            },
        },
    },
];

// 从已勾选 key 推导出所选「App-系统-区域」组合（二级叶子节点）
const getSelectedLeafKeys = (keys) => {
    const result = [];
    MOCK_APP_TREE.forEach((app) => {
        (app.children || []).forEach((c) => {
            if ((keys || []).includes(c.key)) result.push(c.key);
        });
    });
    return result;
};

// 叶子节点展示标签：App-系统-区域
const getLeafLabel = (leafKey) => {
    for (let i = 0; i < MOCK_APP_TREE.length; i += 1) {
        const app = MOCK_APP_TREE[i];
        const child = (app.children || []).find((c) => c.key === leafKey);
        if (child) return `${app.title}-${child.title}`;
    }
    return leafKey;
};

const getSegmentLabel = (code) => {
    const seg = MOCK_USER_SEGMENTS.find((s) => s.value === code);
    return seg ? seg.label : code;
};

const getSegmentCount = (code) => {
    const seg = MOCK_USER_SEGMENTS.find((s) => s.value === code);
    return seg ? seg.count : 0;
};

// 展开 appConfigs 为 [{ code, cooldown, slots }]
const flattenConfigs = (appConfigs) => {
    const list = [];
    Object.entries(appConfigs || {}).forEach(([code, seg]) => {
        list.push({ code, cooldown: seg?.cooldown ?? 0, slots: seg?.slots || [] });
    });
    return list;
};

// ══════════════════════════════════════════════
//  子组件：策略编辑抽屉
// ══════════════════════════════════════════════
const StrategyFormDrawer = ({ open, editingRecord, occupiedLeafMap, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const [submitting, setSubmitting] = useState(false);
    const [checkedKeys, setCheckedKeys] = useState([]);
    // appConfigs：{ segmentCode: { cooldown, slots: [{key, slotId, slotName, appSources:{leafKey:sourceName}, enabled, freq, interval, refresh}] } }
    // 广告位与频控配置为全局一份；广告源按「App-系统-区域」组合分别配置（非必填，留空则该组合不生效）
    const [appConfigs, setAppConfigs] = useState({});
    // 分群内广告位类型去重错误标记 { segmentCode: { slotKey: true } }
    const [typeDupErrors, setTypeDupErrors] = useState({});

    React.useEffect(() => {
        if (open) {
            if (editingRecord) {
                form.setFieldsValue({ name: editingRecord.name });
                setCheckedKeys(editingRecord.appKeys || []);
                setAppConfigs(toInternal(editingRecord.appConfigs || {}));
            } else {
                form.resetFields();
                setCheckedKeys([...DEFAULT_CHECKED_KEYS]);
                setAppConfigs({});
            }
            setTypeDupErrors({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, editingRecord, form]);

    const toInternal = (appConfigs) => {
        const result = {};
        Object.entries(appConfigs).forEach(([code, seg]) => {
            result[code] = {
                cooldown: seg.cooldown ?? 0,
                slots: (seg.slots || []).map((s, i) => ({ ...s, key: i })),
            };
        });
        return result;
    };

    const getSlotTypeLabel = (slotId) => {
        const slot = MOCK_GLOBAL_SLOTS.find((s) => s.slotId === slotId);
        return slot ? AD_TYPE_MAP[slot.type]?.label || slot.type : '';
    };

    // 分群下拉：按「分群 + App-系统-区域」组合过滤已被占用的分群；与当前所选组合冲突的分群不可选
    const getSegmentOptions = () => MOCK_USER_SEGMENTS.map((s) => {
        const occupiedLeaves = occupiedLeafMap.get(s.value);
        const conflict = occupiedLeaves && selectedLeafs.some((leaf) => occupiedLeaves.has(leaf));
        return {
            value: s.value,
            label: `${s.label}（${s.count.toLocaleString()}人）`,
            disabled: conflict,
        };
    });

    const handleCheck = (keys) => setCheckedKeys(keys);
    const handleCheckAll = () => setCheckedKeys([...ALL_APP_KEYS]);
    const handleClearAll = () => setCheckedKeys([]);

    // 当前所选「App-系统-区域」组合（二级叶子节点）
    const selectedLeafs = getSelectedLeafKeys(checkedKeys);

    // 分群复选：新增分群初始化默认配置，移除分群清理配置与类型去重错误
    const updateSegments = (codes) => {
        const nextCodes = codes || [];
        setAppConfigs((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((code) => {
                if (!nextCodes.includes(code)) delete next[code];
            });
            nextCodes.forEach((code) => {
                if (!next[code]) next[code] = { cooldown: 0, slots: [] };
            });
            return next;
        });
        setTypeDupErrors((prev) => {
            const next = { ...prev };
            Object.keys(next).forEach((code) => {
                if (!nextCodes.includes(code)) delete next[code];
            });
            return next;
        });
    };

    const removeSegment = (code) => {
        setAppConfigs((prev) => {
            const next = { ...prev };
            delete next[code];
            return next;
        });
        setTypeDupErrors((prev) => {
            const next = { ...prev };
            delete next[code];
            return next;
        });
    };

    const updateCooldown = (code, value) => {
        setAppConfigs((prev) => ({
            ...prev,
            [code]: { ...prev[code], cooldown: value },
        }));
    };

    // 分群内广告位类型去重校验
    const validateTypeDups = (code, configs) => {
        const errors = {};
        const typeCount = {};
        configs.forEach((c) => {
            if (c.slotId) {
                const slot = MOCK_GLOBAL_SLOTS.find((s) => s.slotId === c.slotId);
                if (slot) typeCount[slot.type] = (typeCount[slot.type] || 0) + 1;
            }
        });
        configs.forEach((c) => {
            const slot = c.slotId ? MOCK_GLOBAL_SLOTS.find((s) => s.slotId === c.slotId) : null;
            if (slot && typeCount[slot.type] > 1) errors[c.key] = true;
        });
        setTypeDupErrors((prev) => ({
            ...prev,
            [code]: errors,
        }));
    };

    const addSlot = (code) => {
        setAppConfigs((prev) => ({
            ...prev,
            [code]: {
                ...prev[code],
                slots: [...(prev[code].slots || []), { key: Date.now(), slotId: '', slotName: '', appSources: {}, enabled: false, freq: '', interval: '', refresh: '' }],
            },
        }));
    };

    const removeSlot = (code, slotKey) => {
        setAppConfigs((prev) => {
            const slots = (prev[code].slots || []).filter((s) => s.key !== slotKey);
            validateTypeDups(code, slots);
            return {
                ...prev,
                [code]: { ...prev[code], slots },
            };
        });
    };

    const updateSlot = (code, slotKey, field, value) => {
        setAppConfigs((prev) => {
            const slots = (prev[code].slots || []).map((s) => (s.key === slotKey ? { ...s, [field]: value } : s));
            if (field === 'slotId') {
                const slot = MOCK_GLOBAL_SLOTS.find((a) => a.slotId === value);
                const idx = slots.findIndex((s) => s.key === slotKey);
                if (slot && idx > -1) {
                    slots[idx].slotName = slot.name;
                    slots[idx].appSources = {};
                }
                validateTypeDups(code, slots);
            }
            return {
                ...prev,
                [code]: { ...prev[code], slots },
            };
        });
    };

    // 更新某广告位下单个「App-系统-区域」组合的广告源（非必填，清空则移除该组合的广告源）
    const updateSlotSource = (code, slotKey, leafKey, value) => {
        setAppConfigs((prev) => {
            const slots = (prev[code].slots || []).map((s) => {
                if (s.key !== slotKey) return s;
                const appSources = { ...(s.appSources || {}) };
                if (value === undefined || value === null || value === '') {
                    delete appSources[leafKey];
                } else {
                    appSources[leafKey] = value;
                }
                return { ...s, appSources };
            });
            return {
                ...prev,
                [code]: { ...prev[code], slots },
            };
        });
    };

    const handleSubmit = async () => {
        try {
            setSubmitting(true);
            const values = await form.validateFields();
            if (getSelectedLeafKeys(checkedKeys).length === 0) { message.error('请至少选择一个App的「系统-区域」'); setSubmitting(false); return; }
            const codes = Object.keys(appConfigs);
            if (codes.length === 0) {
                message.error('请至少选择一个用户分群'); setSubmitting(false); return;
            }
            for (const code of codes) {
                const seg = appConfigs[code];
                if (seg.cooldown === undefined || seg.cooldown === null || seg.cooldown === '') {
                    message.error(`请为「${getSegmentLabel(code)}」填写开屏/插屏冷却间隔`); setSubmitting(false); return;
                }
                const slots = seg.slots || [];
                if (slots.length === 0) {
                    message.error(`请为「${getSegmentLabel(code)}」至少配置一个广告位`); setSubmitting(false); return;
                }
                const hasTypeDup = Object.values(typeDupErrors[code] || {}).some(Boolean);
                if (hasTypeDup) {
                    message.error(`「${getSegmentLabel(code)}」下存在同类型广告位，请更换`); setSubmitting(false); return;
                }
                const occupiedLeaves = occupiedLeafMap.get(code);
                const leafConflict = occupiedLeaves && selectedLeafs.some((leaf) => occupiedLeaves.has(leaf));
                if (leafConflict) {
                    message.error(`「${getSegmentLabel(code)}」分群在当前所选App-系统-区域下已被其他策略配置`); setSubmitting(false); return;
                }
            }
            const strategy = {
                ...values,
                appKeys: checkedKeys,
                appConfigs: Object.fromEntries(
                    Object.entries(appConfigs).map(([code, seg]) => [
                        code,
                        { cooldown: seg.cooldown, slots: seg.slots.map(({ key, ...s }) => s) },
                    ])
                ),
            };
            onSubmit(strategy);
            setSubmitting(false);
        } catch {
            setSubmitting(false);
        }
    };

    return (
        <Drawer
            title={editingRecord ? '编辑广告策略' : '添加广告策略'}
            open={open}
            width={860}
            onClose={onCancel}
            footer={
                <Space style={{ float: 'right' }}>
                    <Button onClick={onCancel}>取消</Button>
                    <Button type="primary" loading={submitting} onClick={handleSubmit}>保存策略</Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical">
                <Form.Item name="name" label="策略名称" rules={[{ required: true, message: '请输入策略名称' }]}>
                    <Input placeholder="请输入策略名称" maxLength={30} />
                </Form.Item>

                {/* 投放App：两级复选（一级 App，二级 系统版本-区域） */}
                <div style={{ background: '#f9f9f9', padding: 16, borderRadius: 8, marginBottom: 16 }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                        <Text strong>投放App</Text>
                        <Space size={4}>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={handleCheckAll}>全选</Button>
                            <Text type="secondary">/</Text>
                            <Button type="link" size="small" style={{ padding: 0 }} onClick={handleClearAll}>清空</Button>
                        </Space>
                    </div>
                    <Tree
                        checkable
                        selectable={false}
                        defaultExpandAll
                        treeData={MOCK_APP_TREE}
                        checkedKeys={checkedKeys}
                        onCheck={handleCheck}
                    />
                    <Alert
                        type="info" showIcon style={{ marginTop: 8, borderRadius: 6 }} banner
                        message="保存后关联App固定，后续新增的平台代运营App不会自动加入本策略；下方广告位与频控配置为全局一份，广告源按「App-系统-区域」组合分别配置（非必填，留空则该组合不生效）。"
                    />
                </div>

                <Divider orientation="left" plain style={{ fontSize: 12, fontWeight: 600, color: '#999' }}>广告配置（广告位与频控全局一份，广告源按App-系统-区域配置）</Divider>

                <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, padding: 14, marginBottom: 12, background: '#fafafa' }}>
                    <Form.Item
                        label="用户分群" required
                        extra="广告位与频控配置为全局一份；广告源按「App-系统-区域」组合分别配置（非必填）；每个分群可独立配置广告位组与冷却间隔；已被其他策略在同一「App-系统-区域」下配置的分群不可选。"
                    >
                        <Select
                            mode="multiple" placeholder="请选择用户分群（必选，可多选）"
                            value={Object.keys(appConfigs)}
                            onChange={updateSegments}
                            options={getSegmentOptions()}
                            optionRender={(option) =>
                                option.data?.disabled ? (
                                    <Tooltip title="该分群在当前所选App-系统-区域下已被其他策略配置">
                                        <span>{option.label}</span>
                                    </Tooltip>
                                ) : option.label
                            }
                        />
                    </Form.Item>

                    {Object.keys(appConfigs).map((code) => {
                        const seg = appConfigs[code];
                        const slots = seg.slots || [];
                        return (
                            <div key={code} style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: 12, marginBottom: 10, background: '#fff' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                                    <Space>
                                        <Tag color="green" style={{ border: 'none', marginRight: 4 }}>{getSegmentLabel(code)}</Tag>
                                        <Text type="secondary" style={{ fontSize: 12 }}>{getSegmentCount(code).toLocaleString()}人</Text>
                                    </Space>
                                    <Button type="link" danger size="small" onClick={() => removeSegment(code)}>移除分群</Button>
                                </div>

                                <Form.Item
                                    label="开屏/插屏冷却间隔（秒）" required
                                    extra="同一用户两次开屏/插屏广告之间的最小时间间隔；0 表示不限制。"
                                >
                                    <InputNumber min={0} placeholder="0" style={{ width: 200 }}
                                        value={seg.cooldown}
                                        onChange={(v) => updateCooldown(code, v)} />
                                </Form.Item>

                                <Divider orientation="left" plain style={{ fontSize: 12, fontWeight: 600, color: '#999', margin: '8px 0' }}>广告位配置（来源：全局广告位库，逐个添加）</Divider>

                                {slots.map((sc) => (
                                    <div key={sc.key} style={{ border: '1px dashed #d9d9d9', borderRadius: 6, padding: 12, marginBottom: 10, background: '#fafafa' }}>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 10 }}>
                                            <div style={{ flex: 1 }}>
                                                <Select
                                                    placeholder="请选择广告位" style={{ width: '100%' }}
                                                    value={sc.slotId || undefined}
                                                    status={typeDupErrors[code]?.[sc.key] ? 'error' : undefined}
                                                    onChange={(v) => updateSlot(code, sc.key, 'slotId', v)}
                                                >
                                                    {MOCK_GLOBAL_SLOTS.map((s) => (
                                                        <Select.Option key={s.slotId} value={s.slotId}>
                                                            {AD_TYPE_MAP[s.type]?.label} · {s.name} ({s.slotId})
                                                        </Select.Option>
                                                    ))}
                                                </Select>
                                                {typeDupErrors[code]?.[sc.key] && (
                                                    <Text type="danger" style={{ fontSize: 12 }}>该分群内已存在同类型广告位（{getSlotTypeLabel(sc.slotId)}），请更换</Text>
                                                )}
                                            </div>
                                            <Button type="link" danger size="small" onClick={() => removeSlot(code, sc.key)} style={{ flexShrink: 0, padding: 0 }}>删除</Button>
                                        </div>

                                        <Form.Item label="广告源（按App-系统-区域配置，非必填，留空则该组合不生效）" style={{ marginBottom: 10 }}>
                                            {!sc.slotId ? (
                                                <Text type="secondary" style={{ fontSize: 12 }}>请先选择广告位</Text>
                                            ) : selectedLeafs.length === 0 ? (
                                                <Text type="secondary" style={{ fontSize: 12 }}>请先在「投放App」中选择系统-区域</Text>
                                            ) : (
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                                                    {selectedLeafs.map((leafKey) => (
                                                        <div key={leafKey} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                            <Text style={{ fontSize: 13, width: 220, flexShrink: 0, textAlign: 'right', color: '#333', whiteSpace: 'nowrap' }}>{getLeafLabel(leafKey)}</Text>
                                                            <Select
                                                                placeholder="请选择广告源（可选）"
                                                                style={{ flex: 1 }}
                                                                value={sc.appSources?.[leafKey] || undefined}
                                                                onChange={(v) => updateSlotSource(code, sc.key, leafKey, v)}
                                                                allowClear
                                                            >
                                                                {(MOCK_GLOBAL_SLOTS.find((a) => a.slotId === sc.slotId)?.sources || []).map((src) =>
                                                                    <Select.Option key={src.adId} value={src.name}>{src.name} ({src.adId})</Select.Option>
                                                                )}
                                                            </Select>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </Form.Item>

                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                                            <Text style={{ fontSize: 13 }}>展示状态</Text>
                                            <Switch checked={sc.enabled} onChange={(v) => updateSlot(code, sc.key, 'enabled', v)} />
                                            <Text type="secondary" style={{ fontSize: 13 }}>{sc.enabled ? '已开启' : '已关闭'}</Text>
                                        </div>

                                        {sc.enabled && (
                                            <div style={{ display: 'flex', gap: 12 }}>
                                                <div style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>频次上限（次/小时）</Text>
                                                    <InputNumber min={0} placeholder="5" style={{ width: '100%' }} value={sc.freq} onChange={(v) => updateSlot(code, sc.key, 'freq', v)} />
                                                    <Text type="secondary" style={{ fontSize: 11 }}>0 表示不限制</Text>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>展示间隔（秒）</Text>
                                                    <InputNumber min={0} placeholder="30" style={{ width: '100%' }} value={sc.interval} onChange={(v) => updateSlot(code, sc.key, 'interval', v)} />
                                                    <Text type="secondary" style={{ fontSize: 11 }}>0 表示不限制</Text>
                                                </div>
                                                <div style={{ flex: 1 }}>
                                                    <Text style={{ fontSize: 12, color: '#999', display: 'block', marginBottom: 4 }}>刷新间隔（秒）</Text>
                                                    <InputNumber min={0} placeholder="60" style={{ width: '100%' }} value={sc.refresh} onChange={(v) => updateSlot(code, sc.key, 'refresh', v)} />
                                                    <Text type="secondary" style={{ fontSize: 11 }}>0 表示不限制</Text>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}

                                {slots.length === 0 && (
                                    <Alert type="warning" showIcon style={{ marginBottom: 10, borderRadius: 6 }} message="请为该分群至少配置一个广告位" />
                                )}

                                <Button type="dashed" size="small" icon={<PlusOutlined />} block onClick={() => addSlot(code)}>
                                    添加广告位
                                </Button>
                            </div>
                        );
                    })}

                    {Object.keys(appConfigs).length === 0 && (
                        <Alert type="info" showIcon style={{ marginBottom: 10, borderRadius: 6 }} message="请至少选择一个用户分群（必填，不可为空）" />
                    )}
                </div>
            </Form>
        </Drawer>
    );
};

// ══════════════════════════════════════════════
//  主组件：广告投放策略
// ══════════════════════════════════════════════
const GlobalAdStrategy = () => {
    const [strategies, setStrategies] = useState(MOCK_STRATEGIES);
    const [keyword, setKeyword] = useState('');
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [editingStrategy, setEditingStrategy] = useState(null);

    const handleAdd = () => { setEditingStrategy(null); setDrawerOpen(true); };
    const handleEdit = (record) => { setEditingStrategy(record); setDrawerOpen(true); };

    const handleSubmit = (values) => {
        if (editingStrategy) {
            setStrategies((prev) => prev.map((s) => (s.key === editingStrategy.key ? { ...s, ...values, strategyId: editingStrategy.strategyId } : s)));
            message.success('编辑成功');
        } else {
            const newStrategy = { key: Date.now().toString(), strategyId: 'test-gstr-' + String(Date.now()).slice(-5), ...values };
            setStrategies((prev) => [...prev, newStrategy]);
            message.success('策略创建成功');
        }
        setDrawerOpen(false);
    };

    const handleDelete = (record) => {
        Modal.confirm({
            title: '确定删除该策略吗？',
            icon: <ExclamationCircleFilled />,
            content: '删除后，该策略占用的「分群 + App-系统-区域」组合将被释放，其他策略可重新配置。',
            okText: '确定删除', okType: 'danger', cancelText: '取消',
            onOk: () => { setStrategies((prev) => prev.filter((s) => s.key !== record.key)); message.success('已删除'); },
        });
    };

    // 已占用「分群 + App-系统-区域」组合（组合唯一），编辑时排除自身策略
    // 返回 Map<segmentCode, Set<leafKey>>
    const buildOccupiedLeafMap = (excludeKey) => {
        const map = new Map();
        strategies.forEach((s) => {
            if (excludeKey && s.key === excludeKey) return;
            const leafKeys = getSelectedLeafKeys(s.appKeys);
            Object.keys(s.appConfigs || {}).forEach((code) => {
                if (!map.has(code)) map.set(code, new Set());
                const leafSet = map.get(code);
                leafKeys.forEach((leaf) => leafSet.add(leaf));
            });
        });
        return map;
    };

    // 搜索：输入 test 可查到，其他输入查不到
    const filtered = keyword.trim()
        ? strategies.filter((s) => {
            const kw = keyword.trim();
            const hit = s.name.includes(kw) || s.strategyId.includes(kw);
            return hit && kw.includes('test');
        })
        : strategies;

    const columns = [
        {
            title: '策略名称', dataIndex: 'name',
            render: (t) => <Text strong>{t}</Text>,
        },
        { title: '策略ID', dataIndex: 'strategyId', render: (t) => <Text copyable code>{t}</Text> },
        {
            title: '关联App', dataIndex: 'appKeys', width: 260,
            render: (appKeys) => {
                const leafKeys = getSelectedLeafKeys(appKeys);
                if (leafKeys.length === 0) return <span style={{ color: '#ccc' }}>—</span>;
                return (
                    <div>
                        {leafKeys.map((k) => (
                            <div key={k} style={{ marginBottom: 2, whiteSpace: 'nowrap' }}>{getLeafLabel(k)}</div>
                        ))}
                    </div>
                );
            },
        },
        {
            title: '分群配置', dataIndex: 'appConfigs', width: 220,
            render: (appConfigs) => {
                if (!appConfigs) return <span style={{ color: '#ccc' }}>—</span>;
                const flat = flattenConfigs(appConfigs);
                if (flat.length === 0) return <span style={{ color: '#ccc' }}>—</span>;
                const tooltip = flat.map((c) =>
                    `${getSegmentLabel(c.code)}（冷却 ${c.cooldown}s）`
                ).join('\n');
                return (
                    <Tooltip title={<pre style={{ margin: 0, fontSize: 12 }}>{tooltip}</pre>}>
                        <div>
                            <Text>{flat.length} 个分群</Text>
                            {flat.slice(0, 2).map((c, i) => (
                                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                    <Tag color="green" style={{ border: 'none', marginBottom: 2, marginRight: 4 }}>{getSegmentLabel(c.code)}</Tag>
                                    <Text type="secondary" style={{ fontSize: 12 }}>{c.cooldown}s</Text>
                                </div>
                            ))}
                            {flat.length > 2 && <Text type="secondary" style={{ fontSize: 12 }}>等 {flat.length} 个分群</Text>}
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: '广告位配置', dataIndex: 'appConfigs', width: 200,
            render: (appConfigs) => {
                if (!appConfigs) return <span style={{ color: '#ccc' }}>—</span>;
                const flat = flattenConfigs(appConfigs);
                if (flat.length === 0) return <span style={{ color: '#ccc' }}>—</span>;
                const totalSlots = flat.reduce((sum, c) => sum + c.slots.length, 0);
                const tooltip = flat.map((c) => {
                    const names = c.slots.map((s) => (s.slotName || '').replace('test-', '').replace('广告位', ''));
                    return `${getSegmentLabel(c.code)}：${names.join('、') || '未配置'}`;
                }).join('\n');
                return (
                    <Tooltip title={<pre style={{ margin: 0, fontSize: 12 }}>{tooltip}</pre>}>
                        <div>
                            <Text strong>{totalSlots}</Text> 个广告位
                            <div><Text type="secondary" style={{ fontSize: 12 }}>覆盖 {flat.length} 个分群</Text></div>
                        </div>
                    </Tooltip>
                );
            },
        },
        {
            title: '操作', dataIndex: 'key',
            render: (_, record) => (
                <Space>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>编辑</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record)}>删除</Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <Breadcrumb style={{ marginBottom: 12 }} items={[{ title: '运营管理' }, { title: '广告投放策略' }]} />
            <Title level={4} style={{ marginBottom: 16 }}>广告投放策略</Title>

            <Alert
                type="info" showIcon closable style={{ marginBottom: 16, borderRadius: 8 }}
                message="广告投放策略按「用户分群」维度配置，广告位与频控配置为全局一份、所有已选App统一生效，广告源按「App-系统-区域」组合分别配置（非必填）；每个分群可独立配置广告位组与开屏/插屏冷却间隔；「分群 + App-系统-区域」组合在策略表中唯一，未命中分群的用户不投放广告。"
            />

            <Card bordered={false} style={{ marginBottom: 8, borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <Form layout="inline">
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space.Compact>
                            <Select
                                defaultValue="name"
                                style={{ width: 110 }}
                                options={[{ value: 'name', label: '策略名称' }, { value: 'strategyId', label: '策略ID' }]}
                            />
                            <Input
                                placeholder="输入 test 查询演示数据"
                                allowClear
                                style={{ width: 240 }}
                                value={keyword}
                                onChange={(e) => setKeyword(e.target.value)}
                            />
                        </Space.Compact>
                    </Form.Item>
                    <Form.Item style={{ marginBottom: 0 }}>
                        <Space>
                            <Button type="primary">查询</Button>
                            <Button onClick={() => setKeyword('')}>重置</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)' }}>
                <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text type="secondary">共 {filtered.length} 条记录</Text>
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>添加策略</Button>
                </div>
                <Table columns={columns} dataSource={filtered} rowKey="key" bordered={false} scroll={{ x: 1100 }}
                    pagination={filtered.length > 10 ? { showSizeChanger: true, showTotal: (t) => `共 ${t} 条记录` } : false}
                    locale={{ emptyText: '未查询到相关数据' }}
                />
            </Card>

            <StrategyFormDrawer
                open={drawerOpen}
                editingRecord={editingStrategy}
                occupiedLeafMap={buildOccupiedLeafMap(editingStrategy?.key)}
                onCancel={() => setDrawerOpen(false)}
                onSubmit={handleSubmit}
            />
        </div>
    );
};

export default GlobalAdStrategy;
